import {createSelector} from '@reduxjs/toolkit'

import {first, includeList} from '@shared/helpers/array'
import {includes, sortBy} from '@shared/helpers/collection'
import {isValid} from '@shared/helpers/date'
import {eq, isEmpty} from '@shared/helpers/lang'

import {
    Card,
    DELIVERY_MEMO,
    DeliveryFeePay,
    DeliveryFeePayType,
    DeliveryTagTypeValues,
    FDSAbuseCode,
    FDSInspectionCode,
    MerchantChannelServiceType,
    PayMethod,
    ProductClass,
    ProductClassType,
} from '@web/core/constants'
import {getInflowUrl} from '@web/core/urls'
import {getDeliveryIconType, getPayMoneyInfo, isArrivalGuarantee} from '@web/helpers/policies'
import {isMobileDisplay} from '@web/helpers/userAgent'

import {ResultDeposit, ResultProduct, RootState} from './interfaces'
import {getDeliveryInfo, getDeliveryMemoType} from './utils'

export const resultSelector = (state: RootState) => state.result

/** 주문완료 배송비 총액 */
export const totalDeliveryFeeAmountSelector = (state: RootState) => state.result.totalDeliveryFeeAmount

export const deliverySelector = (state: RootState) => state.result.delivery

/** 주문 이후 총 할인 금액 */
export const totalDiscountAmountSelector = (state: RootState) => state.result.totalDiscountAmount

/** 주문 이후 상품 금액 총액 */
export const totalOrderAmountSelector = (state: RootState) => state.result.totalOrderAmount

/** 주문이후 안전결제 수수료 */
export const totalPurchaserCommissionAmountSelector = (state: RootState) => state.result.totalPurchaserCommissionAmount

/** 정기구독 1회차 할인 금액 */
export const firstSubscriptionDiscountAmountSelector = (state: RootState) =>
    state.result.firstSubscriptionDiscountAmount

/** 주문 모델 */
export const resultOrderSelector = createSelector(resultSelector, ({order}) => order)

/** 주문 번호 */
export const orderNoSelector = (state: RootState) => state.result.order.orderNo

/** 배송 메모 개별 입력 여부 */
export const deliveryMemoParticularInputSelector = (state: RootState) => state.result.order.deliveryMemoParticularInput

/** 주문 옵션 입력 여부 */
export const orderOptionInfoParticularInputSelector = (state: RootState) =>
    state.result.order.orderOptionInfoParticularInput

/** 결제 모델 */
export const resultPaySelector = (state: RootState) => state.result.pay

/** 충전포인트 결제액 */
export const chargePointPayAmountSelector = (state: RootState) => state.result.pay.chargePointPayAmount

/** 주 결제수단 결제액 */
export const generalPayAmountSelector = (state: RootState) => state.result.pay.generalPayAmount

/** 나중에 결제 결제액 */
export const resultPayLaterAmountSelector = (state: RootState) => state.result.pay.payLaterAmount

/** 적립포인트 결제액 */
export const rewardPointPayAmountSelector = (state: RootState) => state.result.pay.rewardPointPayAmount

/** 프로모션 결제 여부 */
export const isCardPromotionUsedSelector = (state: RootState) => state.result.pay.isCardPromotionUsed

/** 총 결제 결제액 */
export const resultTotalPayAmountSelector = (state: RootState) => state.result.pay.totalPayAmount

/** 결제 승인 모델 */
export const payAdmissionSelector = (state: RootState) => state.result.pay.payAdmission

/** 승인 일시 */
export const admissionDateTimeSelector = (state: RootState) => state.result.pay.payAdmission.admissionDateTime

/** 쇼핑 라이브 정보 */
export const shoppingLiveSelector = (state: RootState) => state.result.shoppingLive

export const talkChannelIdSelector = (state: RootState) => state.result.talkChannelId

/** 카드 결제 모델 */
export const cardSettleInfoSelector = createSelector(
    (state: RootState) => state.result.pay.payAdmission.cardSettleInfo,
    ({cardCompanyName, maskedCardNumber, cardType}) => {
        return {
            cardCompanyName: cardType === Card.NFC_PREPAID ? 'N페이 머니카드' : cardCompanyName,
            maskedCardNumber,
        }
    },
)

/** 무이자 할부 결제 모델 */
export const freeInterestInstallmentInfoSelector = (state: RootState) =>
    state.result.pay.payAdmission.freeInterestInstallmentInfo

/** 모바일 결제 모델 */
export const mobileSettleInfoSelector = (state: RootState) => state.result.pay.payAdmission.mobileSettleInfo
/** 실시간 계좌 이체 결제 모델 */
export const realTimeAccountTransferSettleSelector = (state: RootState) =>
    state.result.pay.payAdmission.realTimeAccountTransferSettle
/** 무통장 입금 결제 모델 */
export const virtualAccountSettleInfoSelector = (state: RootState) =>
    state.result.pay.payAdmission.virtualAccountSettleInfo

/** 주문완료 충전결제 금액 */
export const rechargePointPayAmountSelector = (state: RootState) => state.result.pay.rechargePointPayAmount

/** 나중에 결제기한 */
export const payExpirationDateTimeSelector = (state: RootState) =>
    state.result.pay.payAdmission.virtualAccountSettleInfo?.payExpirationDateTime || ''

/** 승인된 결제 수단(주 결제수단 만을 포함) */
export const approvedPayMethodTypeSelector = (state: RootState) => state.result.pay.payAdmission.payMethodType

/** 결제 방법 */
export const payMethodTypeSelector = (state: RootState) => state.result.pay.payMethodType

/** 보증금 모델 */
export const depositSelector = createSelector(resultSelector, ({deposit}): ResultDeposit => {
    return {
        coolBag: deposit?.coolBag ?? false,
        totalCoolBagDepositAmount: deposit?.totalCoolBagDepositAmount ?? 0,
        reusableBag: deposit?.reusableBag ?? false,
        totalReusableBagDepositAmount: deposit?.totalReusableBagDepositAmount ?? 0,
        plasticBag: deposit?.plasticBag ?? false,
        totalPlasticBagAmount: deposit?.totalPlasticBagAmount ?? 0,
    }
})

/** 주문완료 선택된 결제수단 */
export const resultSelectedPayMethodTypeSelector = createSelector(payMethodTypeSelector, (selectedMethod) => {
    const equalsSelectedMethod = eq(selectedMethod)
    const isSelectedGeneralCreditCard = equalsSelectedMethod(PayMethod.CARD)
    const isSelectedGeneralMobile =
        equalsSelectedMethod(PayMethod.MOBILE) || equalsSelectedMethod(PayMethod.MOBILE_EASY_PAY)
    const isSelectedGeneralSkip = equalsSelectedMethod(PayMethod.SKIP)
    const isSelectedGeneral = isSelectedGeneralCreditCard || isSelectedGeneralMobile || isSelectedGeneralSkip
    const isSelectedEasyBank = equalsSelectedMethod(PayMethod.BANK_EASY_PAY)
    const isSelectedEasyCard = equalsSelectedMethod(PayMethod.CARD_EASY_PAY)
    const isSelectedVirtualAccount = equalsSelectedMethod(PayMethod.VIRTUAL_ACCOUNT)
    const isSelectedFamilyPay = equalsSelectedMethod(PayMethod.FAMILY_PAY)
    const isSelectedEasyPay = isSelectedEasyBank || isSelectedEasyCard

    return {
        /** 일반결제 > 신용카드 결제 여부 */
        isSelectedGeneralCreditCard,
        /** 일반결제 > 모바일 결제 여부 */
        isSelectedGeneralMobile,
        /** 일반결제 > 나중에 결제 여부 */
        isSelectedGeneralSkip,
        /** 일반결제 여부 */
        isSelectedGeneral,
        /** 계좌 간편결제 여부 */
        isSelectedEasyBank,
        /** 카드 간편결제 여부 */
        isSelectedEasyCard,
        /** 간편결제 여부 */
        isSelectedEasyPay,
        /** 무통장입금 선택 여부 */
        isSelectedVirtualAccount,
        /** 패밀리결제 선택 여부 */
        isSelectedFamilyPay,
    }
})

/** 구매자 정보 */
export const purchaserSelector = createSelector(resultSelector, ({purchaser}) => ({
    ...purchaser,
    ...getPayMoneyInfo(purchaser.chargePointUserStatusType),
}))

/** 시스템 점검으로 결제대기 중 여부 */
export const isAsyncPayWaitingSelector = (state: RootState) => state.result.pendingPayment

/** TODO 주문완료 배너 */
export const resultBannersSelector = () =>
    [] as {
        link: string
        imageUrl: string
    }[]

export const afterOrderProductsSelector = createSelector(
    (state: RootState) => state.result.products,
    (products) =>
        products.map((product) => {
            const quantity = products.reduce((prev, next) => {
                return prev + next.orderQuantity
            }, 0)

            return {
                ...product,
                orderAmount: product.totalOrderAmount,
                quantity,
            }
        }),
)

export const productOrderNosSelector = createSelector(
    (state: RootState) => state.result.products,
    (products) => products.map((product) => product.productOrderNo),
)

/** E쿠폰 바코드연동한 판매자의 E쿠폰 상품이 포함되었는지 여부를 반환 */
export const includesECouponBarcodeSelector = createSelector(resultSelector, ({products}) =>
    products.some(({isEcoupon, ecouponBarcodeConnected}) => [isEcoupon, ecouponBarcodeConnected].every(eq(true))),
)

/** DeliveryGroup 으로 묶은 주문 이후 상품들 */
export const afterOrderDeliveryGroupSelector = createSelector(
    afterOrderProductsSelector,
    resultSelector,
    (products, {delivery: {deliveryGroupsByKey}}) => {
        const deliveryGroup: Array<
            ResultProduct & {
                deliveryFee: number
                deliveryFeePayType: DeliveryFeePayType
            }
        >[] = []
        products.forEach((product) => {
            // 배송지 정보가 없다면, NOTHING 으로 처리
            const deliveryGroupInfo = deliveryGroupsByKey[product.deliveryGroupKey] ?? {
                deliveryFee: 0,
                deliveryFeePayType: DeliveryFeePay.FREE,
            }

            const index =
                deliveryGroup.findIndex((deliveryGroupProducts) =>
                    deliveryGroupProducts.some(({deliveryGroupKey}) => product.deliveryGroupKey === deliveryGroupKey),
                ) ?? deliveryGroup.length

            const result = {
                ...product,
                deliveryFee: deliveryGroupInfo.deliveryFee,
                deliveryFeePayType: deliveryGroupInfo.deliveryFeePayType,
            }

            if (index > -1) {
                deliveryGroup[index]?.push(result)
            } else {
                deliveryGroup.push([result])
            }
        })

        const productItemSize = products.reduce((acc, {items}) => {
            const optionalTypes: ProductClassType[] = [ProductClass.OPTIONAL, ProductClass.PRICING_OPTIONAL]
            const hasPricingOptionalItem = items.some(({productClassType}) => optionalTypes.includes(productClassType))
            const size = items.length
            // 옵션 수 + 상품 (priceOptional이 item에 포함되어있으면 product 본체를 추가하지 않고, 없다면 본체를 세기 위해 +1)
            return acc + size + (hasPricingOptionalItem ? 0 : 1)
        }, 0)

        return {
            productsByDeliveryGroup: deliveryGroup,
            productItemSize,
        }
    },
)

/** 주문완료 상품별 추가 정보 모델 */
export const additionalInfoGroupsSelector = (state: RootState) =>
    state.result.products.map(({additionalInfos}) => additionalInfos)

export const deliveryInfoGroupsSelector = (state: RootState) =>
    state.result.products.map(({deliveryMemo = '', deliveryMethodType = '', dispatchDueDateTime = ''}) => ({
        deliveryMemo,
        deliveryMethodType,
        dispatchDueDateTime,
    }))

/** 주문완료 배송그룹 정보 */
export const resultDeliveryGroupInfoSelector = (state: RootState) => state.result.delivery.deliveryGroupsByKey

/** 주문완료 토스트배너 정보 */
export const toastBannerInfoSelector = (state: RootState) => state.result.toastPopup

export const isMembershipPointLoadingSelector = (state: RootState) => state.result.isMembershipPointLoading

export const aitemsParameterInfoSelector = createSelector(
    (state: RootState) => state.result.products,
    (products) => {
        return {
            productOrderList: products.map(({ecNvMid, productCategoryId}) => {
                return {
                    ecNvMid,
                    productCategoryId,
                }
            }),
        }
    },
)

/**
 * 주문완료 > 배송메모 개별 입력 정보
 * - 주문서와 달리 주문완료에는 product.items에 본 상품이 포함되지 못함 (single product)
 * - 본상품들에 대한 배송 메모 정보 + items를 가진 상품들에 대한 배송메모 정보
 */
export const afterProductMemoInfosSelector = createSelector(afterOrderProductsSelector, (productGroups) => {
    return productGroups.reduce<
        {productName: string; options: string; memo: string; productOrderNo: string; templateMemo: boolean}[]
    >((acc, product) => {
        const singleProductInfo = {
            productOrderNo: product.productOrderNo,
            productName: product.productName,
            options: '',
            memo: product.deliveryMemo,
            templateMemo: product.templateMemo,
        }

        /** Single Product일때 */
        if (isEmpty(product.items) || product.items.some(({productClassType}) => productClassType === 'SINGLE')) {
            return [...acc, singleProductInfo]
        }

        /**
         * items가 SUPPLEMENTED로만 이루어진 상품일때
         * 본상품 메모정보 + items들의 메모정보
         */
        if (product.items.every((item) => item && item.productClassType === 'SUPPLEMENTED')) {
            return [
                ...acc,
                singleProductInfo,
                ...product.items.map(({optionContents = '', deliveryMemo, productOrderNo, templateMemo}) => {
                    const optionContent = optionContents ?? ''
                    return {
                        // 옵션명과 조합하지 않은 productName
                        productOrderNo,
                        productName: optionContent,
                        options: '',
                        memo: deliveryMemo ?? '',
                        templateMemo,
                    }
                }),
            ]
        }

        /** items에 옵션상품이 포함된 경우 */
        return [
            ...acc,
            ...product.items.map(
                ({optionContents = '', productClassType, deliveryMemo, productOrderNo, templateMemo}) => {
                    const optionContent = optionContents ?? ''
                    const memoContent = deliveryMemo ?? ''
                    if (productClassType === 'SUPPLEMENTED') {
                        return {
                            // 옵션명과 조합하지 않은 productName
                            productOrderNo,
                            productName: optionContent,
                            options: '',
                            memo: memoContent,
                            templateMemo,
                        }
                    }
                    return {
                        // 옵션명과 조합하지 않은 productName
                        productOrderNo,
                        productName: product.productName,
                        options: optionContent,
                        memo: memoContent,
                        templateMemo,
                    }
                },
            ),
        ]
    }, [])
})

/** 배송 메모 존재 여부 */
export const hasDeliveryMemoSelector = createSelector(afterProductMemoInfosSelector, (memoInfos) =>
    memoInfos.some(({memo}) => memo),
)

/** 개별 입력 배송 메모 배열 */
export const particularDeliveryMemosSelector = createSelector(afterProductMemoInfosSelector, (memoInfos) => {
    return memoInfos.map(({memo}) => memo)
})

/** 통합 입력 배송 메모 */
export const integratedDeliveryMemoSelector = createSelector(particularDeliveryMemosSelector, (memos) => memos[0])

/** 배송정보 변경 메모 info selector */
export const deliveryChangeMemoSelector = createSelector(
    resultSelector,
    afterProductMemoInfosSelector,
    ({order: {deliveryMemoParticularInput}, products}, productMemos) => {
        const mainProduct = products[0]
        return {
            memo: {
                type: mainProduct
                    ? getDeliveryMemoType(mainProduct.deliveryMemo, mainProduct.templateMemo)
                    : DELIVERY_MEMO.NOTHING,
                value: mainProduct?.deliveryMemo ?? '',
            },
            productMemos: productMemos.map(({productOrderNo, productName, options, memo, templateMemo}) => {
                return {
                    productOrderNo,
                    productName,
                    optionContents: options,
                    deliveryMemo: {
                        type: getDeliveryMemoType(memo, templateMemo),
                        value: memo,
                    },
                }
            }),
            isParticularInput: deliveryMemoParticularInput,
            saveAsDefaultMemo: false,
        }
    },
)

const DefaultMerchant = {name: '', merchantUrl: ''} as const

export const ProductInfoSelector = createSelector(
    resultSelector,
    afterOrderDeliveryGroupSelector,
    ({merchantsByMerchantNo}, {productsByDeliveryGroup, productItemSize}) => {
        const merchantGroups = productsByDeliveryGroup.map((group) => {
            // 대표 상품
            const mainProduct = group[0]

            const merchant =
                (mainProduct?.merchantNo && merchantsByMerchantNo[mainProduct.merchantNo]) || DefaultMerchant
            const merchantName = merchant.name
            const merchantURL = merchant.merchantUrl
            const channelText = [
                ...(mainProduct?.merchantChannelServiceType
                    ? [`[${MerchantChannelServiceType[mainProduct.merchantChannelServiceType]}]`]
                    : []),
                ...(mainProduct?.merchantChannelName ? [mainProduct.merchantChannelName] : []),
            ].join(' ')
            const todayDispatch = group[0]?.todayDispatch
            return {
                products: group.map((product) => {
                    return {
                        ...product,
                        arrivalGuaranteeDateTime: product.arrivalGuarantee?.arrivalGuaranteeDateTime,
                    }
                }),
                merchantName,
                merchantURL,
                merchantNo: mainProduct?.merchantNo,
                ...(mainProduct?.branch
                    ? {
                          branch: {
                              name: mainProduct.branch.name,
                              delivery: mainProduct.branch.delivery,
                          },
                      }
                    : {}),
                /** 희망배송일 */
                hopeDelivery: group[0]?.hopeDelivery ?? null,
                ...(group?.[0]?.deliveryAttributeType
                    ? {
                          deliveryAttributeType: group[0].deliveryAttributeType,
                      }
                    : null),
                /** 예약 구매 정보 */
                preOrder: group?.[0]?.preOrder
                    ? {
                          deliveryStartDateTime: group[0].preOrder.deliveryStartDateTime,
                          deliveryEndDateTime: group[0].preOrder.deliveryEndDateTime,
                          minimumOrderQuantity: group[0].preOrder.minimumOrderQuantity,
                      }
                    : undefined,
                /** //TODO [🚧 orderResultNext] 아래 값들은 임시 값(스마트스토어 작업시 추가 필요) */
                sameDayDispatch: null,
                deliveryStartDate: 1,
                deliveryEndDate: 2,
                isEmailAddressExpose: true,
                orderEndDate: 2,
                estimatedDeliveryDate: 1,
                // deliveryProbability: 99,
                // channelType: OrderEntry.SHOPN,
                // channelName: 'BELE',
                isOverseasDelivery: mainProduct?.overseasDelivery ?? false,
                customOrderDetailType: {
                    text: null,
                },
                // desiredDeliveryInfo: {
                //     region: '',
                //     deliveryFee: '',
                //     desiredDate: '',
                //     includesTime: true,
                //     type: DesiredDeliveryCase.STATUS,
                // },
                channelText,
                todayDispatch:
                    todayDispatch && todayDispatch.todayDispatch === true
                        ? {
                              basisTime: (function (date) {
                                  if (date) {
                                      return `${date.slice(0, 2)}:${date.slice(2, 4)}`
                                  }
                                  return date
                              })(todayDispatch.todayDispatchBasisTime),
                              notAppliedReason: todayDispatch.todayDispatchNotAppliedReason,
                          }
                        : null,
            }
        })
        /**
         * 직거래 유도 어뷰징 의심 판매자 존재여부. 처벌단계까지 진행되지 않은 어뷰징 의심 판매자.
         * 어뷰즈 코드가 다양하므로 어뷰즈 판매자에 대한 반응 추가시에는 인지된 어뷰즈 코드에 대한 동작만 처리되도록 코드 추가할 것
         */
        const directDealBlackListedMerchantExists = Object.values(merchantsByMerchantNo).some(
            (merchant) =>
                merchant.penaltyStatus === FDSInspectionCode.OBSERVED &&
                merchant.abusingCode === FDSAbuseCode.MERCHANT_DIRECT_DEAL_ABUSE,
        )

        return {
            merchantGroups,
            totalItemCount: productItemSize,
            directDealBlackListedMerchantExists,
        }
    },
)

export const ecouponBarcodeSelector = createSelector(
    resultSelector,
    ({
        deliveryNothingOrder,
        order: {preOrderIncluded, todayDispatchIncluded},
        pay: {payMethodType},
        products,
    }): boolean => {
        const isEcouponIncluded = products.some(
            ({isEcoupon, ecouponBarcodeConnected}) => isEcoupon && ecouponBarcodeConnected,
        )
        const isPayNext = includes(payMethodType, [PayMethod.SKIP, PayMethod.VIRTUAL_ACCOUNT])
        return deliveryNothingOrder && isEcouponIncluded && !isPayNext && !preOrderIncluded && !todayDispatchIncluded
    },
)

/**
 * 상품 정렬 로직
 * - 우선순위: 배송 속성 > 배송일 빠른순 > 상품 주문번호 순 > 배송일 문구 없는 상품
 * - 배송 속성 우선순위: 도착보장 > 오늘출발 > 배송 시뮬레이터 > 배송 희망일 > 예약구매 > 주문확인 후 제작
 * - 배송 속성이 있더라도, 문구를 만들 수 없는 경우 최하위 정렬 우선순위를 가진다.
 * - 장보기의 경우 장보기 상품끼리만 주문할 수 있으므로 제외한다 (장보기 상품과 다른 배송속성 상품이 같이 있을 수 없음)
 */
export const ProductInfoNewSelector = createSelector(
    resultSelector,
    isAsyncPayWaitingSelector,
    resultSelectedPayMethodTypeSelector,
    ({products}, isPendingPayment, {isSelectedVirtualAccount, isSelectedFamilyPay}) => {
        const refinedProducts = products.map((product) => {
            const isTodayDispatchUnExposed = isPendingPayment || isSelectedVirtualAccount || isSelectedFamilyPay
            const {priority, message, startDate, endDate} = getDeliveryInfo({product, isTodayDispatchUnExposed})
            return {
                ...product,
                productUrl: getInflowUrl({
                    no: product.merchantNo.toString(),
                    tr: 'co',
                    pType: isMobileDisplay ? 'M' : 'P',
                    retUrl: product?.productUrl ?? '',
                }),
                /** 배송 속성 우선순위 */
                deliveryPriority: priority,
                /** 배송일 안내문구 */
                deliveryDateMessage: message,
                /** 배송일 시작 */
                deliveryDateStart: isValid(startDate) ? startDate : null,
                /** 배송일 마감기한 */
                deliveryDateEnd: isValid(endDate) ? endDate : null,
                /** 배송태그 */
                deliveryIconType: getDeliveryIconType({
                    orderDate: product.orderDateTime,
                    deliveryTagType: product.deliveryTagType,
                }),
            }
        })

        const sortedProducts = sortBy(
            [
                (product) => !product.deliveryDateMessage, // message가 없는 것은 최하순위
                (product) => product.deliveryPriority, // 배송 속성 우선순위
                (product) => product.deliveryDateEnd, // 배송일 빠른 순
                (product) => product.deliveryDateStart, // 배송일이 기간인 경우, 마감시간이 같으면 시작시간을 고려한다
                (product) => product.productOrderNo, // 모두 같은경우 상품번호 순
            ],
            refinedProducts,
        )

        const hasProduct = !isEmpty(sortedProducts)

        const mainProduct = (hasProduct && sortedProducts[0]) || null

        const productItemCount = sortedProducts.reduce((acc, {items}) => {
            const optionalTypes: ProductClassType[] = [ProductClass.OPTIONAL, ProductClass.PRICING_OPTIONAL]
            const hasPricingOptionalItem = items.some(({productClassType}) => optionalTypes.includes(productClassType))
            const hasSingleItem = items.some(({productClassType}) => productClassType === ProductClass.SINGLE)
            const size = items.length

            return hasSingleItem ? 1 : acc + size + (hasPricingOptionalItem ? 0 : 1)
        }, 0)

        const hasOverseaWithArrivalGuarantee = products.every(
            ({deliveryAttributeType, overseasDelivery}) =>
                !isArrivalGuarantee(deliveryAttributeType) || !overseasDelivery,
        )

        return {
            products: sortedProducts,
            hasProduct,
            mainProduct,
            productItemCount,
            hasOverseaWithArrivalGuarantee,
            deliveryTagTypes: products.reduce((acc, product) => {
                const deliveryTag = product.deliveryTagType

                if (deliveryTag) {
                    return [...acc, deliveryTag]
                }

                return acc
            }, [] as DeliveryTagTypeValues[]),
        }
    },
)

/** 대표 상품 */
export const productMainInfoSelector = createSelector(
    ProductInfoNewSelector,
    resultSelector,
    ({mainProduct, productItemCount}, {merchantsByMerchantNo}) => {
        const productName = mainProduct?.productName || ''
        const productImageUrl = mainProduct?.productImageUrl || ''
        const merchantNo = mainProduct?.merchantNo || 0
        const merchantName = merchantsByMerchantNo[merchantNo]?.name || ''
        const merchantUrl = merchantsByMerchantNo[merchantNo]?.merchantUrl || ''
        const productShortName = `${productName.substring(0, 10)}${productName.length > 10 ? '...' : ''}`
        const merchantShortName = `${merchantName.substring(0, 10)}${merchantName.length > 10 ? '...' : ''}`

        return {
            productName,
            productShortName:
                productItemCount > 1 ? `${productShortName} 외 ${productItemCount - 1}건` : productShortName,
            productImageUrl,
            merchantNo,
            merchantName,
            merchantShortName,
            merchantUrl,
        }
    },
)

/** 선물하기 정보 */
export const giftingReceiverInfoSelector = createSelector(
    (state: RootState) => state.result.gifting,
    ({receiverCount, productOrderGiftingInfos}) => {
        const receivers = [...productOrderGiftingInfos].sort((a, b) => (a.keyType < b.keyType ? -1 : 1))
        const {name, dueDateTime, giftingBoxShortUrl = '', idNo, senderNickName = ''} = first(receivers) ?? {}

        const [hasNaver, hasPhone, hasKakao] = includeList({
            list: productOrderGiftingInfos,
            checks: ['RCVTP_001', 'RCVTP_002', 'RCVTP_003'],
            callback: ({keyType}) => keyType,
        })

        const naverUsers = receivers.reduce((users: Record<string, string[]>, receiver) => {
            if (receiver.keyType === 'RCVTP_001') {
                users[receiver.idNo] = users[receiver.idNo]
                    ? [...(users[receiver.idNo] ?? []), receiver.productOrderNo]
                    : [receiver.productOrderNo]
            }
            return users
        }, {})

        return {
            type: (function () {
                if (hasNaver && hasPhone) {
                    return 'NAVER_PHONE'
                }
                if (hasNaver) {
                    return 'NAVER'
                }
                if (hasPhone) {
                    return 'PHONE'
                }
                if (hasKakao) {
                    return 'KAKAO'
                }
                return ''
            })(),
            receiverName: name ?? '',
            receiverIdNo: idNo,
            senderName: senderNickName,
            date: dueDateTime ?? '',
            count: receiverCount,
            naverUserCount: Object.keys(naverUsers).length,
            receivers,
            // https://m.gift.naver.com/${shortUrlCode}
            kakaoLinkPath: `${giftingBoxShortUrl}`,
        }
    },
)

export const shoppingLogSelector = createSelector(resultSelector, ({products: originalProducts}) => {
    const products: ResultProduct[] = originalProducts.map((product) => {
        const hasSingleItem = product.items.every((item) => item.productOrderNo !== product.productOrderNo)

        return {
            ...product,
            ...(hasSingleItem && {
                items: [
                    {
                        productOrderNo: product.productOrderNo,
                        productClassType: ProductClass.SINGLE,
                        price: 0,
                        quantity: 0,
                        optionContents: null,
                        deliveryMemo: null,
                        templateMemo: false,
                        elements: null,
                    },
                    ...product.items,
                ],
            }),
        }
    })

    return {
        products,
    }
})

/**
 * 주문완료 > 각각의 금액을 계산하는 selector
 * 각각의 사용처에서 별도로 계산하지 않기 위해 하나로 묶는다.
 */
export const amountSelector = createSelector(
    totalOrderAmountSelector,
    resultTotalPayAmountSelector,
    totalDeliveryFeeAmountSelector,
    deliverySelector,
    totalDiscountAmountSelector,
    totalPurchaserCommissionAmountSelector,
    firstSubscriptionDiscountAmountSelector,
    depositSelector,
    (
        productTotalAmount,
        totalPayAmount,
        deliveryAmount,
        {deliveryGroupsByKey},
        discountAmount,
        purchaserCommissionAmount,
        firstSubscriptionDiscountAmount,
        {totalCoolBagDepositAmount, totalPlasticBagAmount},
    ) => {
        const deliveryDiscountAmount = Object.values(deliveryGroupsByKey).reduce(
            (acc, deliveryGroup) => acc + (deliveryGroup.deliveryFeeDiscountAmount ?? 0),
            0,
        )

        return {
            /** 주문 이후 상품 금액 총액 */
            productTotalAmount,
            /** 총 결제 결제 금액 */
            totalPayAmount,
            /** 주문완료 배송비 총액 */
            deliveryAmount,
            /** 주문완료 배송비 할인 총액 */
            deliveryDiscountAmount,
            /** 주문 이후 총 할인 금액 */
            discountAmount,
            /** 주문이후 안전결제 수수료 */
            purchaserCommissionAmount,
            /** 알비백 보증금 금액 */
            totalCoolBagDepositAmount,
            /** 정기구독 1회차 할인금액 */
            firstSubscriptionDiscountAmount,
            /** 총 결제금액 */
            totalAmount:
                productTotalAmount +
                deliveryAmount -
                discountAmount +
                purchaserCommissionAmount +
                (totalCoolBagDepositAmount + totalPlasticBagAmount) -
                firstSubscriptionDiscountAmount,
        }
    },
)
